<template>
	<SlidingSheet
		:is-open="isOpen"
		:title="`Integration Failures - ${serviceName}`"
		width="800px"
		@close="$emit('close')"
	>
		<template #content>
			<!-- Loading skeleton -->
			<div
				v-if="loading"
				class="failures-list"
			>
				<div
					v-for="n in 3"
					:key="`skeleton-${n}`"
					class="failure-skeleton"
				>
					<div class="skeleton-header">
						<div class="skeleton-date" />
						<div class="skeleton-code" />
					</div>
					<div class="skeleton-system" />
					<div class="skeleton-message" />
				</div>
			</div>
			<!-- Actual failures -->
			<div
				v-else-if="failures && failures.length > 0"
				class="failures-list"
			>
				<div
					v-for="failure in failures"
					:key="failure.failureID"
					class="failure-item"
				>
					<div class="failure-header">
						<span class="failure-date">
							{{ failure.responseDate }}
						</span>
						<span
							class="failure-code"
							:class="getCodeClass(failure.responseCode)"
						>
							{{ failure.responseCode }}
						</span>
					</div>
					<div class="failure-system">
						<strong>
							System:
						</strong> {{ failure.systemName }}
					</div>
					<div class="failure-response">
						<strong>
							Response Message:
						</strong>
						<div class="json-editor-container">
							<JsonEditor
								:value="formatResponseMessage(failure.responseMessage)"
								:disabled="true"
								placeholder="No detailed response message available"
								theme="default"
							/>
						</div>
					</div>
				</div>
			</div>
			<div
				v-else
				class="no-failures"
			>
				<p>
					No failures found for this integration.
				</p>
			</div>
		</template>

		<template #footer>
			<div class="footer-content">
				<SecondaryActionButton @click="$emit('close')">
					Close
				</SecondaryActionButton>
				<div class="error-count">
					Total errors: {{ failures.length }}
				</div>
			</div>
		</template>
	</SlidingSheet>
</template>

<script>
import { SlidingSheet, JsonEditor } from '../../../components/component-library-wrapper.js'
import SecondaryActionButton from '../../../../../shared/components/secondary-action-button.vue'
import { getIntegrationFailures } from '../../../../../shared/utils/api/log-viewer.js'
import { formatResponseMessage } from '../../../../../shared/utils/jsonFormatter.js'

export default {
	components: { SlidingSheet, JsonEditor, SecondaryActionButton },
	props: {
		isOpen: {
			type: Boolean,
			default: false
		},
		serviceName: {
			type: String,
			required: true
		},
		integrationId: {
			type: [String, Number],
			required: true
		}
	},
	data () {
		return {
			failures: [],
			loading: false
		}
	},
	watch: {
		isOpen (newVal) {
			if (newVal && this.integrationId) {
				// Load failures when sheet opens
				this.loadFailures()
			}
		},
		integrationId (newVal) {
			if (newVal && this.isOpen) {
				// Load failures when integration ID changes while sheet is open
				this.loadFailures()
			}
		}
	},
	mounted () {
		if (this.isOpen && this.integrationId) {
			this.loadFailures()
		}
	},
	methods: {
		getCodeClass (responseCode) {
			if (responseCode >= 400 && responseCode < 500) {
				return 'code-client-error'
			} else if (responseCode >= 500) {
				return 'code-server-error'
			}
			return 'code-other'
		},
		formatResponseMessage (responseMessage) {
			return formatResponseMessage(responseMessage)
		},
		async loadFailures () {
			this.loading = true
			try {
				const { data: { failures } } = await getIntegrationFailures(null, this.integrationId)
				this.failures = failures
			} catch (error) {
				console.error('Failed to load integration failures:', error)
				this.failures = []
			} finally {
				this.loading = false
			}
		}
	}
}
</script>

<style scoped>
.failures-list {
	/* overflow-y: auto; */
}

.failure-item {
	border: 1px solid var(--global-border-color);
	border-radius: var(--radius-sm, 4px);
	padding: 12px;
	margin-bottom: 12px;
	background-color: var(--global-main-background-color);
}

.failure-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 8px;
}

.failure-date {
	font-size: 14px;
	color: var(--global-foreground);
}

.failure-code {
	padding: 2px 8px;
	border-radius: var(--radius-sm, 4px);
	font-weight: bold;
	font-size: 12px;
}

.code-client-error {
	background-color: #fff3cd;
	color: #856404;
}

.code-server-error {
	background-color: #f8d7da;
	color: #721c24;
}

.code-other {
	background-color: #d1ecf1;
	color: #0c5460;
}

.failure-system {
	margin-bottom: 8px;
	font-size: 14px;
}

.failure-response {
	margin-top: 12px;
	font-size: 14px;
}

.no-failures {
	text-align: center;
	padding: 40px 20px;
	color: var(--global-foreground);
}

.footer-content {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
}

.error-count {
	font-size: 14px;
	color: var(--global-foreground);
	font-weight: 500;
}

.json-editor-container {
	margin-top: 8px;
	border: 1px solid #e9ecef;
	border-radius: var(--radius-sm, 4px);
	overflow: hidden;
}

.json-editor-container >>> .json-editor__container {
	min-height: 100px;
	max-height: 300px;
}

.json-editor-container >>> .CodeMirror {
	height: auto;
	min-height: 100px;
	max-height: 300px;
	font-size: 12px;
	background: var(--global-main-background-color);
}

.json-editor-container >>> .CodeMirror-scroll {
	overflow-y: auto;
	max-height: 300px;
}

.json-editor-container >>> .CodeMirror.cm-s-default {
	background: var(--global-main-background-color);
	color: var(--global-foreground);
}

.json-editor-container >>> .CodeMirror-cursor {
	display: none !important;
}

/* Skeleton loader styles */
.failure-skeleton {
	border: 1px solid var(--global-border-color);
	border-radius: var(--radius-sm, 4px);
	padding: 12px;
	margin-bottom: 12px;
	background-color: var(--global-main-background-color);
	animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 8px;
}

.skeleton-date {
	width: 140px;
	height: 14px;
	background-color: var(--global-border-color);
	border-radius: var(--radius-sm, 4px);
}

.skeleton-code {
	width: 40px;
	height: 20px;
	background-color: var(--global-border-color);
	border-radius: var(--radius-sm, 4px);
}

.skeleton-system {
	width: 60%;
	height: 14px;
	background-color: var(--global-border-color);
	border-radius: var(--radius-sm, 4px);
	margin-bottom: 8px;
}

.skeleton-message {
	width: 80%;
	height: 14px;
	background-color: var(--global-border-color);
	border-radius: var(--radius-sm, 4px);
}

@keyframes pulse {
	0% {
		opacity: 1;
	}
	50% {
		opacity: 0.6;
	}
	100% {
		opacity: 1;
	}
}
</style>
